import { useContext, forwardRef, ReactNode, useId, useState, useEffect, useRef, useCallback } from 'react';
import type { ChangeEventHandler, ComponentPropsWithoutRef, FocusEventHandler, MutableRefObject } from 'react';
import { formInlineContext } from '../../visual-helper/FormInline';
import { twMerge } from 'tailwind-merge';
import { gridPlacementClasses } from '@/chassis/core/common-helpers/class-merge';
import FormLabel from '../FormLabel';
import RequiredBadge from '../../visual-helper/RequiredBadge';
import { useLanguage } from '@/chassis/ui/i18n/useLanguage';
import type { Language } from '@/chassis/ui/i18n/i18n-helper';
import { REQUIRED_FIELD_MESSAGE, REQUIRED_VALIDATION_EVENT, type RequiredValidationEventDetail } from '@/chassis/data/services/form-required-validation';
import { COMBO_CLASS } from './combo';

type I18nLabel = Record<Language, string>;
type I18nLike = string | I18nLabel;

const toI18nLabel = (value?: I18nLike, lang?: Language): string | undefined => {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    return lang ? value[lang] : undefined;
};

interface FormSelectOption {
    label?: ReactNode;
    i18nLabel?: I18nLike;
    value: string | number;
    disabled?: boolean;
}

interface FormSelectProps extends ComponentPropsWithoutRef<'select'> {
    formSelectSize?: 'sm' | 'lg';
    label?: string;
    i18nLabel?: I18nLike;
    wrapperClassName?: string;
    options?: ReadonlyArray<FormSelectOption>;
    requiredMessage?: string;
}

const FormSelect = forwardRef<HTMLSelectElement, FormSelectProps>((props, ref) => {
    const formInline = useContext(formInlineContext);
    const lang = useLanguage();
    const {
        formSelectSize,
        label,
        i18nLabel,
        wrapperClassName,
        id,
        name,
        className,
        children,
        options = [],
        onBlur,
        onChange,
        requiredMessage = REQUIRED_FIELD_MESSAGE,
        ...rest
    } = props;
    const { ['aria-describedby']: ariaDescribedByProp, ...restProps } = rest as typeof rest & {
        'aria-describedby'?: string;
    };
    const isRequired = Boolean((restProps as { required?: boolean }).required);
    const [showRequiredError, setShowRequiredError] = useState(false);
    const selectRef = useRef<HTMLSelectElement | null>(null);
    const generatedId = useId();
    const controlId = id || (name as string | undefined) || generatedId;
    const errorId = `${controlId}-required`;

    const mergeRefs = useCallback(
        (node: HTMLSelectElement | null) => {
            selectRef.current = node;
            if (typeof ref === 'function') {
                ref(node);
            } else if (ref) {
                (ref as MutableRefObject<HTMLSelectElement | null>).current = node;
            }
        },
        [ref],
    );
    
    // Use i18nLabel if provided, otherwise fall back to label
    const displayLabel = toI18nLabel(i18nLabel, lang) ?? label;

    const isEmptyValue = (value: unknown) => value === null || value === undefined || value === '';

    const handleBlur: FocusEventHandler<HTMLSelectElement> = (event) => {
        if (isRequired) {
            setShowRequiredError(isEmptyValue(event.target.value));
        }
        onBlur?.(event);
    };

    const handleChange: ChangeEventHandler<HTMLSelectElement> = (event) => {
        if (isRequired && showRequiredError && !isEmptyValue(event.target.value)) {
            setShowRequiredError(false);
        }
        onChange?.(event);
    };

    useEffect(() => {
        if (!isRequired) return undefined;

        const listener = (event: Event) => {
            const detail = (event as CustomEvent<RequiredValidationEventDetail>).detail;
            const targetForm = detail?.form;
            const element = selectRef.current;

            if (!element) return;
            if (targetForm && element.form && targetForm !== element.form) return;

            if (isEmptyValue(element.value)) {
                setShowRequiredError(true);
            }
        };

        window.addEventListener(REQUIRED_VALIDATION_EVENT, listener as EventListener);
        return () => window.removeEventListener(REQUIRED_VALIDATION_EVENT, listener as EventListener);
    }, [isRequired]);

    const ariaDescribedBy = [ariaDescribedByProp, showRequiredError ? errorId : null]
        .filter(Boolean)
        .join(' ') || undefined;

    const selectEl = (
        <select
            ref={mergeRefs}
            id={controlId}
            name={name}
            {...restProps}
            onBlur={handleBlur}
            onChange={handleChange}
            aria-invalid={showRequiredError ? 'true' : undefined}
            aria-describedby={ariaDescribedBy}
            className={
                twMerge([
                    // Combo padrão do design system (29/10), ver ./combo.ts.
                    COMBO_CLASS,
                    'disabled:dark:bg-darkmode-700/50',
                    '[&[readonly]]:bg-slate-100 [&[readonly]]:cursor-not-allowed [&[readonly]]:dark:bg-darkmode-700/50',
                    formSelectSize == 'sm' && 'h-8 text-xs py-1 pl-2 pr-8',
                    formSelectSize == 'lg' && 'h-12 text-lg py-1.5 pl-4 pr-10',
                    formInline && 'flex-1',
                    showRequiredError && 'border-danger focus:border-danger focus:ring-danger/30',
                    className,
                ])
            }
        >
            {children}
            {options.map((option) => (
                <option key={`${option.value}`} value={option.value} disabled={option.disabled}>
                    {option.i18nLabel ? toI18nLabel(option.i18nLabel, lang) : option.label}
                </option>
            ))}
        </select>
    );

    const helpText = showRequiredError ? (
        <p id={errorId} className="mt-1 text-xs text-danger">
            {requiredMessage}
        </p>
    ) : null;

    if (!displayLabel)
        return (
            <>
                {selectEl}
                {helpText}
            </>
        );

    return (
        <div className={twMerge(['w-full', gridPlacementClasses(className), wrapperClassName])}>
            <FormLabel htmlFor={controlId}>
                <span className="inline-flex min-h-[1.25rem] items-center gap-1">
                    <span>{displayLabel}</span>
                    {isRequired && <RequiredBadge />}
                </span>
            </FormLabel>
            {selectEl}
            {helpText}
        </div>
    );
});

export { FormSelect };
export default FormSelect;
export type { FormSelectOption, FormSelectProps };
